
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { CustomFormField } from "./FormField";
import { contactFormSchema, FormValues } from "./ContactFormSchema";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useState } from "react";

export const ContactForm = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const form = useForm<FormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      businessName: "",
      contactName: "",
      email: "",
      phone: "",
    },
  });

  const onSubmit = async (data: FormValues) => {
    if (isSubmitting) return; // Prevent duplicate submissions
    
    try {
      setIsSubmitting(true);
      console.log('Starting form submission with data:', data);
      
      // Log the request first
      const { error: logError } = await supabase
        .from('request_logs')
        .insert([
          {
            action_type: 'contact_form_submission',
            ip_address: 'client-side'
          }
        ]);

      if (logError) {
        console.error('Error logging request:', logError);
        throw new Error('Failed to log request: ' + logError.message);
      }

      // Add email to subscribers list
      const { error: subscriberError } = await supabase
        .from('email_subscribers')
        .insert([
          {
            email: data.email,
            source: 'pro_contact_form',
            subscription_type: ['newsletter', 'pro_updates'],
            page_url: window.location.pathname,
          }
        ]);

      // Only throw if it's not a duplicate email error (code 23505)
      if (subscriberError && subscriberError.code !== '23505') {
        console.error('Error adding subscriber:', subscriberError);
        throw new Error('Failed to add to subscribers: ' + subscriberError.message);
      }

      // Send email using Supabase Edge Function
      const { data: emailData, error: emailError } = await supabase.functions.invoke('send-email', {
        body: {
          type: 'contact',
          data: {
            businessName: data.businessName,
            contactName: data.contactName.trim(),
            email: data.email.trim(),
            phone: data.phone.trim()
          }
        }
      });

      if (emailError) {
        console.error('Error sending email:', emailError);
        throw new Error('Failed to send email notification: ' + emailError.message);
      }

      console.log('Email sent successfully:', emailData);

      toast({
        title: "Success!",
        description: "We've received your information and will be in touch soon.",
      });

      form.reset();
    } catch (error: any) {
      console.error('Error submitting form:', error);
      toast({
        title: "Error",
        description: error.message || "There was a problem submitting your information. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <Card className="border-primary/20">
        <CardHeader className="text-center bg-gradient-to-r from-primary/5 to-primary/10">
          <CardTitle className="text-2xl font-bold text-primary">Submit Your Business</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <CustomFormField
                control={form.control}
                name="businessName"
                label="Business Name"
              />
              
              <CustomFormField
                control={form.control}
                name="contactName"
                label="Contact Name"
              />
              
              <CustomFormField
                control={form.control}
                name="email"
                label="Email"
                type="email"
              />
              
              <CustomFormField
                control={form.control}
                name="phone"
                label="Phone Number"
                type="tel"
              />

              <Button 
                type="submit" 
                className="w-full bg-primary hover:bg-primary/90 text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Get Listed Today"}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};
